import React, { useMemo, useState } from 'react'
import { get, isEmpty, upperFirst } from 'lodash'

import { RequirementsModal } from 'civic-champs-shared/question-sets/components'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { invitationTypeToActionLookup } from 'civic-champs-shared/question-sets/types'
import HeaderWrapper from 'civic-champs-shared/core/HeaderWrapper'
import StepContainer from 'civic-champs-shared/core/StepContainer'
import { useAdminLogin } from 'invite/hooks/useAdminLogin'
import useIssueLoginToken from 'civic-champs-shared/api/hooks/useIssueLoginToken'

import Loading from '../../../core/Loading'
import { useAcceptOrRejectInvite, useSignUp, useUpdateProfile, useUpdateOrg } from '../../hooks'
import AccountCreationCompletion from './AccountCreationCompletion'
import StepOne from './StepOne'
import StepTwo from './StepTwo'
import StepZero from './StepZero'
import Review from './Review'
import { skipNextAuthEvent } from 'auth/useUserStatus'

export default function InviteForm(props) {
  const loginAdmin = useAdminLogin()
  const [issueLoginToken] = useIssueLoginToken({})
  const { invite, children, initialStep = 1 } = props
  const steps = useMemo(() => {
    const steps =
      initialStep === 0
        ? ['Log-in', 'Additional Information', 'Verify']
        : ['Account', 'Forms (if applicable)', 'Verify']
    if (initialStep === 0) steps.unshift('Organization')
    return steps
  }, [initialStep])
  const isVolunteerAccountFlow = initialStep !== 0
  const [acceptOrRejectInvite] = useAcceptOrRejectInvite(invite)
  const [updateProfile] = useUpdateProfile(invite)
  const [organization, setOrganization] = useState(invite.organization)
  const signUp = useSignUp(invite)
  const [step, setStep] = useState(initialStep)
  const [updateOrg] = useUpdateOrg(invite)
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [profile, setProfile] = useState({
    givenName: upperFirst(get(invite, 'person.givenName') || ''),
    familyName: upperFirst(get(invite, 'person.familyName') || ''),
    email: get(invite, 'person.email') || '',
    phoneNumber: get(invite, 'person.phoneNumber') || '',
    homePhoneNumber: get(invite, 'person.homePhoneNumber') || '',
    olderThanThirteen: false,
  })
  const [confirmedProfile, setConfirmedProfile] = useState({})
  const fulfillRequirementsPrompt = useShowPrompt(RequirementsModal)

  const handleRequirements = async user => {
    try {
      const done = await fulfillRequirementsPrompt({
        person: user,
        filters: {
          personId: user.id,
          invitationToken: invite.secureToken,
          action: get(invitationTypeToActionLookup, invite.invitationType, 'user_sign_up'),
          freshAnswerSet: false,
          excludeCompletedAnswerSets: true,
          excludeCredentials: false,
        },
        isAdmin: false,
        organizationId: invite.organization.id,
      })
      return done
    } catch (error) {
      console.error('error', error)
    }
    return true
  }

  const onSubmitStepZero = ({ address, name, website, timeZone }) => {
    const org = {
      address: {
        line1: address.line1,
        line2: address.line2,
        state: address.state,
        city: address.city,
        country: address.country,
        zip: address.zip,
        gps: address.geofencing.location.coordinates,
      },
      name,
      website,
      timeZone,
    }
    setOrganization(org)
    updateOrg(org).then(() => setStep(1))
  }
  const onSubmitStepOne = async values => {
    if (isVolunteerAccountFlow) {
      const { password: nextPassword, passwordConfirm, givenName, familyName, olderThanThirteen } = values
      setPassword(nextPassword)
      setProfile(current => ({
        ...current,
        givenName,
        familyName,
        olderThanThirteen,
      }))
      setStep(2)
      if (await handleRequirements(invite.person)) {
        setStep(3)
      } else {
        setStep(1)
      }
      return
    }

    setPassword(values.password)
    setStep(2)
  }

  const onSubmitStepTwo = async profile => {
    setProfile(profile)
    if (await handleRequirements(invite.person)) {
      setStep(3)
    }
  }
  const handleBack = () => {
    if (isVolunteerAccountFlow && step === 3) {
      setStep(1)
      return
    }

    setStep(step => step - 1)
  }

  const onSubmitStepReview = async () => {
    setIsSubmitting(true)
    try {
      // important that 'updateProfile' goes first, so that we can mark the email or phone as 'verified',
      // which is checked during the preSignup hook
      const { olderThanThirteen, ...rest } = profile
      const updatedProfile = await updateProfile(rest)
      await signUp({ ...updatedProfile, password })
      // import that 'acceptOrRejectInvite' goes after 'signUp' in case 'signUp' fails,
      // as the user cannot do anything once an invite is accepted
      await acceptOrRejectInvite('accept')

      if (initialStep === 0) {
        skipNextAuthEvent('signIn')
        await loginAdmin(rest.email, password)
        const { token } = await issueLoginToken({ organizationId: invite.organization.id })

        window.location = `${process.env.REACT_APP_ADMIN_SITE}/?token=${token}`

        // avoid enabling action buttons before redirect by awaiting promise that never resolves
        await new Promise(() => {})
      } else {
        setConfirmedProfile(profile)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const isAccountCreated = !isEmpty(confirmedProfile)

  return (
    <HeaderWrapper title={isAccountCreated ? 'Thank You!' : 'Create Account'}>
      {!invite ? (
        <Loading />
      ) : isAccountCreated ? (
        <AccountCreationCompletion organization={organization} profile={confirmedProfile} password={password} />
      ) : (
        <>
          {step !== 1 && step !== 3 && children}
          {step === 1 && isVolunteerAccountFlow && (
            <div className="invite-header">
              <p style={{ textAlign: 'center' }}>
                Welcome to <strong>{organization.name}</strong>!
              </p>
              <p style={{ textAlign: 'center' }}>
                Edit your name, create a password, and confirm your age for your Civic Champs account below.
              </p>
            </div>
          )}
          {step === 3 && (
            <div className="invite-header">
              <p style={{ textAlign: 'center' }}>
                You're almost done making your account for <strong>{organization.name}</strong>!
              </p>
              <p style={{ textAlign: 'center' }}>
                Please review your details below to make sure everything is correct before finalizing your Civic Champs
                account.
              </p>
            </div>
          )}
          <StepContainer steps={steps} activeStep={step - initialStep}>
            {step === 0 && <StepZero invite={invite} onSubmit={onSubmitStepZero}></StepZero>}
            {step === 1 && (
              <StepOne
                onBack={initialStep === 0 ? handleBack : undefined}
                password={password}
                profile={profile}
                combinedAccountStep={isVolunteerAccountFlow}
                invite={invite}
                onSubmit={onSubmitStepOne}
              ></StepOne>
            )}
            {step === 2 && !isVolunteerAccountFlow && (
              <StepTwo
                onChange={setProfile}
                onBack={handleBack}
                invite={invite}
                profile={profile}
                onSubmit={onSubmitStepTwo}
              />
            )}
            {step === 3 && (
              <Review
                onBack={handleBack}
                isSubmitting={isSubmitting}
                organization={organization}
                profile={profile}
                password={password}
                invite={invite}
                initialStep={initialStep}
                onSubmit={onSubmitStepReview}
              />
            )}
          </StepContainer>
        </>
      )}
    </HeaderWrapper>
  )
}
